import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, vShow as _vShow, withDirectives as _withDirectives, unref as _unref, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "app-tag-selector__header" }
const _hoisted_2 = { class: "app-tag-selector__search" }
const _hoisted_3 = {
  key: 0,
  class: "app-tag-selector__body app-tag-selector__body--loading"
}
const _hoisted_4 = {
  key: 1,
  class: "app-tag-selector__body"
}
const _hoisted_5 = { class: "app-tag-selector__section-title" }
const _hoisted_6 = { class: "app-tag-selector__section-title-text" }
const _hoisted_7 = {
  key: 1,
  class: "app-tag-selector__empty"
}
const _hoisted_8 = {
  key: 2,
  class: "app-tag-selector__body app-tag-selector__body--columns"
}
const _hoisted_9 = {
  key: 0,
  class: "app-tag-selector__columns"
}
const _hoisted_10 = {
  key: 0,
  class: "app-tag-selector__section app-tag-selector__section--system"
}
const _hoisted_11 = { class: "app-tag-selector__section-title" }
const _hoisted_12 = {
  key: 1,
  class: "app-tag-selector__section-right"
}
const _hoisted_13 = { class: "app-tag-selector__section-title" }
const _hoisted_14 = { class: "app-tag-selector__section-title" }
const _hoisted_15 = {
  key: 1,
  class: "app-tag-selector__empty"
}

import {
	ref,
	toRef,
	computed,
	watch,
	onMounted,
	nextTick
} from 'vue';
import { debounce } from 'lodash';
import AppCheckbox from '@/components/ui-kit/checkbox/AppCheckbox.vue';
import AppTagSelectorList from './AppTagSelectorList.vue';
import AppInput from '@/components/ui-kit/input/AppInput.vue';
import AppIcon from '@/components/ui-kit/icon/AppIcon.vue';
import AppLoader from '@/components/common/AppLoader.vue';
import AppTooltip from '@/components/ui-kit/tooltip/AppTooltip.vue';
import type { TagWithFrequency, TagContainer } from '@/models/interfaces/tags/tag-models';
import type { TagCategory } from './types';
import { TagEntityType } from '@/models/etc/types';
import { useTagList } from '@/composables/useTagsCache';
import { i18n } from '@/i18n';

type AppTagSelectorProps = {
	modelValue?: TagWithFrequency[];
	categories?: TagCategory[];
	entityType?: TagEntityType;
	placeholder?: string;
	mode?: 'filter' | 'select';
	allowCreate?: boolean;
	allowSelectGroup?: boolean;
};

type AppTagSelectorEmits = {
	'update:modelValue': [tags: TagWithFrequency[]];
	'reset': [];
	'close': [];
	'create-tag': [name: string];
};


export default /*@__PURE__*/_defineComponent({
  __name: 'AppTagSelector',
  props: {
    modelValue: { default: () => [] },
    categories: { default: () => [] },
    entityType: {},
    placeholder: { default: '' },
    mode: { default: 'filter' },
    allowCreate: { type: Boolean, default: false },
    allowSelectGroup: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "reset", "close", "create-tag"],
  setup(__props: any, { emit: __emit }) {

const entityLabelKeys: Record<number, string> = {
	[TagEntityType.Person]: 'tags_of_contacts',
	[TagEntityType.Client]: 'tags_of_clients',
	[TagEntityType.Project]: 'tags_of_projects',
	[TagEntityType.File]: 'tags_of_files',
	[TagEntityType.Job]: 'tags_of_jobs',
};

const props = __props;

const emit = __emit;

const searchQuery = ref('');
const debouncedQuery = ref('');

const searchInputRef = ref<typeof AppInput | null>(null);

onMounted(async () => {
	await nextTick();
	searchInputRef.value?.focusInput();
});

// список тегов держит общий кеш: при повторном открытии он отдаётся сразу,
// а свежие данные подтягиваются фоном и вливаются точечно
const { container: tagContainer, loading } = useTagList(toRef(props, 'entityType'));

const applySearchQuery = debounce((value: string) => {
	debouncedQuery.value = value;
}, 300);

watch(searchQuery, value => applySearchQuery(value));

const clearSearch = () => {
	applySearchQuery.cancel();
	searchQuery.value = '';
	debouncedQuery.value = '';
};

const buildCategoriesFromContainer = (container: TagContainer): TagCategory[] => {
	const labelKey = entityLabelKeys[props.entityType!] || 'ui_all_tags';
	const label = String(i18n.global.t(labelKey));
	if (props.mode === 'filter') {
		return [{ key: 'entity', label, tags: container.OftenUsedForEntityTags }];
	}
	return [
		{ key: 'system', label: String(i18n.global.t('selector_system_tags')), tags: container.AllSystemTags },
		{ key: 'entity', label, tags: container.OftenUsedForEntityTags },
		{ key: 'user', label: String(i18n.global.t('ui_all_your_tags')), tags: container.OftenUsedByUserTags },
	];
};

const resolvedCategories = computed<TagCategory[]>(() => {
	if (props.entityType != null) {
		return buildCategoriesFromContainer(tagContainer.value);
	}
	return props.categories;
});

const rootClasses = computed(() => ({
	'app-tag-selector': true,
	'app-tag-selector--select': props.mode === 'select',
}));

const selectedIds = computed(() => new Set(props.modelValue.map(t => t.Id)));

const hasSelection = computed(() => props.modelValue.length > 0);

const systemCategory = computed(() =>
	resolvedCategories.value.find(c => c.key === 'system')
);

const entityCategory = computed(() =>
	resolvedCategories.value.find(c => c.key === 'entity')
);

const userCategory = computed(() =>
	resolvedCategories.value.find(c => c.key === 'user')
);

const visibleCategories = computed(() =>
	resolvedCategories.value.filter(c => hasVisibleTags(c))
);

const matchesSearch = (tag: TagWithFrequency, query: string): boolean => {
	if (!query) return true;
	const lowerQuery = query.toLowerCase();
	const name = (tag.FullName || tag.Name || '').toLowerCase();
	return name.includes(lowerQuery);
};

const filteredByKey = computed<Record<string, TagWithFrequency[]>>(() => {
	const result: Record<string, TagWithFrequency[]> = {};
	for (const category of resolvedCategories.value) {
		result[category.key] = category.tags.filter(
			tag => matchesSearch(tag, debouncedQuery.value)
		);
	}
	return result;
});

const getFilteredTags = (category: TagCategory): TagWithFrequency[] => {
	return filteredByKey.value[category.key] ?? [];
};

const hasVisibleTags = (category: TagCategory): boolean => {
	return getFilteredTags(category).length > 0;
};

const hasAnyTags = computed(() => {
	return resolvedCategories.value.some(c => c.tags.length > 0)
})

const isCategoryAllSelected = (category: TagCategory): boolean => {
	if (category.tags.length === 0) {
		return false;
	}
	return category.tags.every(t => selectedIds.value.has(t.Id));
};

const toggleTag = (tag: TagWithFrequency) => {
	const current = [...props.modelValue];
	const index = current.findIndex(t => t.Id === tag.Id);
	if (index >= 0) {
		current.splice(index, 1);
	} else {
		current.push(tag);
	}
	emit('update:modelValue', current);
};

const toggleCategory = (category: TagCategory) => {
	const visibleTags = category.tags;
	const allSelected = isCategoryAllSelected(category);
	let current = [...props.modelValue];

	if (allSelected) {
		const categoryIds = new Set(visibleTags.map(t => t.Id));
		current = current.filter(t => !categoryIds.has(t.Id));
	} else {
		const existingIds = new Set(current.map(t => t.Id));
		for (const tag of visibleTags) {
			if (!existingIds.has(tag.Id)) {
				current.push(tag);
			}
		}
	}

	emit('update:modelValue', current);
};

const resetSelection = () => {
	emit('update:modelValue', []);
	emit('reset');
};

const createTag = () => {
	const name = searchQuery.value.trim();
	if (!name) {
		return;
	}
	emit('create-tag', name.toLowerCase());

	clearSearch();
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(rootClasses.value)
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(AppInput, {
          ref_key: "searchInputRef",
          ref: searchInputRef,
          modelValue: searchQuery.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
          placeholder: _ctx.placeholder || _ctx.$t('search_short'),
          "show-clear-button": "",
          onClearField: clearSearch
        }, {
          "custom-icons": _withCtx(() => [
            (props.allowCreate && props.mode === 'select' && searchQuery.value)
              ? (_openBlock(), _createBlock(AppTooltip, {
                  key: 0,
                  text: _ctx.$t('file_add_tag'),
                  position: "top"
                }, {
                  activator: _withCtx(() => [
                    _createVNode(AppIcon, {
                      name: "plus",
                      width: "16px",
                      color: "#979797",
                      "hover-color": "#499259",
                      cursor: "pointer",
                      onClick: createTag
                    })
                  ]),
                  _: 1
                }, 8, ["text"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["modelValue", "placeholder"]),
        _createElementVNode("div", {
          class: "app-tag-selector__close",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('close')))
        }, [
          _createVNode(AppTooltip, {
            text: _ctx.$t('close_esc_label')
          }, {
            activator: _withCtx(() => [
              _createVNode(AppIcon, {
                class: "app-tag-selector__close-icon",
                name: "close",
                width: "20px",
                color: "#979797",
                "hover-color": "#499259",
                cursor: "pointer"
              })
            ]),
            _: 1
          }, 8, ["text"])
        ])
      ])
    ]),
    _withDirectives(_createElementVNode("span", {
      class: "app-tag-selector__reset",
      onClick: resetSelection
    }, _toDisplayString(_ctx.$t("selector_reset")), 513), [
      [_vShow, !searchQuery.value && hasSelection.value]
    ]),
    (_unref(loading))
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(AppLoader)
        ]))
      : (_ctx.mode === 'filter')
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            (visibleCategories.value.length > 0)
              ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(visibleCategories.value, (category) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: category.key,
                    class: "app-tag-selector__section"
                  }, [
                    _createElementVNode("div", _hoisted_5, [
                      _createElementVNode("div", _hoisted_6, _toDisplayString(category.label), 1)
                    ]),
                    _createVNode(AppTagSelectorList, {
                      tags: getFilteredTags(category),
                      "selected-ids": selectedIds.value,
                      "search-text": debouncedQuery.value,
                      onToggle: toggleTag
                    }, null, 8, ["tags", "selected-ids", "search-text"])
                  ]))
                }), 128))
              : (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(hasAnyTags.value ? _ctx.$t("ui_tag_not_found") : _ctx.$t("ui_tags_empty")), 1))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_8, [
            (visibleCategories.value.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  (systemCategory.value && hasVisibleTags(systemCategory.value))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                        _createElementVNode("div", _hoisted_11, [
                          (_ctx.allowSelectGroup)
                            ? (_openBlock(), _createBlock(AppCheckbox, {
                                key: 0,
                                style: { height: '16px', marginRight: '8px' },
                                value: isCategoryAllSelected(systemCategory.value),
                                text: "",
                                onOnInput: _cache[2] || (_cache[2] = ($event: any) => (toggleCategory(systemCategory.value)))
                              }, null, 8, ["value"]))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", {
                            class: "app-tag-selector__section-title-text",
                            onClick: _cache[3] || (_cache[3] = ($event: any) => (_ctx.allowSelectGroup ? toggleCategory(systemCategory.value) : undefined))
                          }, _toDisplayString(systemCategory.value.label), 1)
                        ]),
                        _createVNode(AppTagSelectorList, {
                          tags: getFilteredTags(systemCategory.value),
                          "selected-ids": selectedIds.value,
                          "search-text": debouncedQuery.value,
                          onToggle: toggleTag
                        }, null, 8, ["tags", "selected-ids", "search-text"])
                      ]))
                    : _createCommentVNode("", true),
                  ((entityCategory.value && hasVisibleTags(entityCategory.value)) || (userCategory.value && hasVisibleTags(userCategory.value)))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                        (entityCategory.value && hasVisibleTags(entityCategory.value))
                          ? (_openBlock(), _createElementBlock("div", {
                              key: 0,
                              class: _normalizeClass({
								'app-tag-selector__section': true,
								'app-tag-selector__section--entity': true,
								'app-tag-selector__section--full': !userCategory.value || !hasVisibleTags(userCategory.value)
							})
                            }, [
                              _createElementVNode("div", _hoisted_13, [
                                (_ctx.allowSelectGroup)
                                  ? (_openBlock(), _createBlock(AppCheckbox, {
                                      key: 0,
                                      style: { height: '16px', marginRight: '8px' },
                                      value: isCategoryAllSelected(entityCategory.value),
                                      text: "",
                                      onOnInput: _cache[4] || (_cache[4] = ($event: any) => (toggleCategory(entityCategory.value)))
                                    }, null, 8, ["value"]))
                                  : _createCommentVNode("", true),
                                _createElementVNode("div", {
                                  class: "app-tag-selector__section-title-text",
                                  onClick: _cache[5] || (_cache[5] = ($event: any) => (_ctx.allowSelectGroup ? toggleCategory(entityCategory.value) : undefined))
                                }, _toDisplayString(entityCategory.value.label), 1)
                              ]),
                              _createVNode(AppTagSelectorList, {
                                tags: getFilteredTags(entityCategory.value),
                                "selected-ids": selectedIds.value,
                                "search-text": debouncedQuery.value,
                                onToggle: toggleTag
                              }, null, 8, ["tags", "selected-ids", "search-text"])
                            ], 2))
                          : _createCommentVNode("", true),
                        (userCategory.value && hasVisibleTags(userCategory.value))
                          ? (_openBlock(), _createElementBlock("div", {
                              key: 1,
                              class: _normalizeClass({
								'app-tag-selector__section': true,
								'app-tag-selector__section--user': true,
								'app-tag-selector__section--full': !entityCategory.value || !hasVisibleTags(entityCategory.value)
							})
                            }, [
                              _createElementVNode("div", _hoisted_14, [
                                (_ctx.allowSelectGroup)
                                  ? (_openBlock(), _createBlock(AppCheckbox, {
                                      key: 0,
                                      style: { height: '16px', marginRight: '8px' },
                                      value: isCategoryAllSelected(userCategory.value),
                                      text: "",
                                      onOnInput: _cache[6] || (_cache[6] = ($event: any) => (toggleCategory(userCategory.value)))
                                    }, null, 8, ["value"]))
                                  : _createCommentVNode("", true),
                                _createElementVNode("div", {
                                  class: "app-tag-selector__section-title-text",
                                  onClick: _cache[7] || (_cache[7] = ($event: any) => (_ctx.allowSelectGroup ? toggleCategory(userCategory.value) : undefined))
                                }, _toDisplayString(userCategory.value.label), 1)
                              ]),
                              _createVNode(AppTagSelectorList, {
                                tags: getFilteredTags(userCategory.value),
                                "selected-ids": selectedIds.value,
                                "search-text": debouncedQuery.value,
                                onToggle: toggleTag
                              }, null, 8, ["tags", "selected-ids", "search-text"])
                            ], 2))
                          : _createCommentVNode("", true)
                      ]))
                    : _createCommentVNode("", true)
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_15, _toDisplayString(hasAnyTags.value ? _ctx.$t("ui_tag_not_found") : _ctx.$t("ui_tags_empty")), 1))
          ]))
  ], 2))
}
}

})